/**
 * RTK Query endpoints for the Klassenbuch Diary sub-feature — Mutations.
 *
 * Write operations: upsert diary entry, quick-confirm attendance.
 * Uses pessimistic cache updates (onQueryStarted) — no invalidatesTags.
 *
 * @module klassenbuch/klassenbuch/api/mutations
 */

import { rootApi } from "../../../../lib/api/rootApi";
import type { DiaryEntry } from "../types/diaryTypes";
// @ts-ignore — legacy .js module without type declarations
import { saveLessonAttendanceBulkRPC } from "../../../../lib/supabase";

export const klassenbuchDiaryMutationApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Upsert a diary entry (topic + homework) for a lesson.
     * Calls: fn_klassenbuch_upsert_diary_entry(p_lesson_id, p_entry_text, p_homework)
     */
    upsertDiaryEntry: builder.mutation<
      DiaryEntry,
      { lessonId: string; entryText: string; homework: string | null }
    >({
      query: ({ lessonId, entryText, homework }) => ({
        type: "rpc" as const,
        rpc: "fn_klassenbuch_upsert_diary_entry",
        params: {
          p_lesson_id: lessonId,
          p_entry_text: entryText,
          p_homework: homework,
        },
      }),
      transformResponse: (response: unknown): DiaryEntry => {
        return response as DiaryEntry;
      },
      // Invalidate cached diary entries so they refetch with the new data
      invalidatesTags: [{ type: "KlassenbuchDiary" as never, id: "ENTRIES" }],
    }),

    /**
     * Quick-confirm attendance: carry-forward all statuses from the previous
     * lesson onto the current lesson, exactly like the AttendanceDialog's
     * "Anwesenheit speichern" button.
     * Calls: save_lesson_attendance_bulk RPC directly.
     */
    quickConfirmAttendance: builder.mutation<
      void,
      {
        lessonId: string;
        schoolId: string;
        attendance: {
          student_id: string;
          status: string;
          note?: string | null;
        }[];
      }
    >({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      queryFn: (async ({ lessonId, schoolId, attendance }: any) => {
        try {
          await saveLessonAttendanceBulkRPC({
            lessonId,
            schoolId,
            attendance,
            diaryText: null,
            diaryPrivate: false,
          });
          return { data: undefined };
        } catch (err: unknown) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              data: String(err),
              error: String(err),
            },
          };
        }
      }) as any,
      // Refresh everything the confirm touches so the UI updates without
      // a manual reload:
      //  - Klaerungen REPORT  → "Mein Klassenbericht" picks up new U marks
      //  - Stundenplan WEEK   → the diary's lesson attendance state
      //  - KlassenbuchDiary   → the "Offene Einträge" lists + badge counts
      invalidatesTags: [
        { type: "Klaerungen" as never, id: "REPORT" },
        { type: "Stundenplan" as never, id: "WEEK" },
        { type: "KlassenbuchDiary" as never, id: "ENTRIES" },
      ],
    }),

    /**
     * Replace the school's "extra subjects that count for offene Einträge"
     * list. Curriculum subjects are ignored server-side (they always count).
     * Admin-only — the RPC asserts the caller is an admin.
     * Calls: fn_klassenbuch_report_subjects_set(p_subject_ids uuid[])
     */
    setReportSubjects: builder.mutation<void, { subjectIds: string[] }>({
      query: ({ subjectIds }) => ({
        type: "rpc" as const,
        rpc: "fn_klassenbuch_report_subjects_set",
        params: { p_subject_ids: subjectIds },
      }),
      // Changing the subject list changes what the missing-items RPCs
      // return — refresh the Offene-Einträge lists and badge counts too.
      invalidatesTags: [
        { type: "Klassenbuch" as never, id: "REPORT_SUBJECTS" },
        { type: "KlassenbuchDiary" as never, id: "MY_MISSING" },
        { type: "KlassenbuchDiary" as never, id: "KL_MISSING" },
        { type: "KlassenbuchDiary" as never, id: "ENTRIES" },
      ],
    }),
  }),
});

export const {
  useUpsertDiaryEntryMutation,
  useQuickConfirmAttendanceMutation,
  useSetReportSubjectsMutation,
} = klassenbuchDiaryMutationApi;
